import { X, User, Activity, Briefcase, Building, Calendar, DollarSign, Palmtree } from 'lucide-react'
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts'
import {
  useColaboradoresGetEmployee,
  type EmployeeListItem,
} from '@/api/colaboradores-hooks'

interface EmployeeDetailDrawerProps {
  employee: EmployeeListItem | null
  onClose: () => void
}

function formatCurrency(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—'
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value)
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '—'
  try {
    return new Date(dateStr).toLocaleDateString('pt-BR')
  } catch {
    return dateStr
  }
}

function formatPeriod(mesano: string): string {
  if (!mesano || mesano.length < 6) return mesano
  const month = mesano.substring(0, 2)
  const year = mesano.substring(2, 6)
  const months = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez']
  const monthIdx = parseInt(month) - 1
  return `${months[monthIdx] || month}/${year.substring(2)}`
}

const CONTRACT_LABELS: Record<string, string> = {
  CLT: 'CLT',
  PJ: 'PJ',
  ESTAGIO: 'Estágio',
  SOCIO: 'Sócio',
  FREELANCER: 'Freelancer',
}

export function EmployeeDetailDrawer({ employee, onClose }: EmployeeDetailDrawerProps) {
  const { data, isLoading } = useColaboradoresGetEmployee(employee?.id ?? null)

  if (!employee) return null

  const payrollChartData = (data?.recentPayroll ?? [])
    .slice()
    .reverse()
    .map(r => ({
      period: formatPeriod(r.mesano),
      cprov: Math.round(Number(r.cprov) || 0),
    }))

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/60 z-40 cursor-pointer motion-safe:animate-[fadeIn_200ms_ease-out]"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer */}
      <div
        role="dialog"
        aria-label={`Detalhes de ${employee.funcionario}`}
        className="fixed right-0 top-0 bottom-0 w-full max-w-lg bg-brand-dark-light border-l border-brand-dark-lighter z-50 overflow-y-auto shadow-2xl motion-safe:animate-[slideInRight_250ms_ease-out]"
      >
        {/* Header */}
        <div className="sticky top-0 bg-brand-dark-light border-b border-brand-dark-lighter px-6 py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-brand-primary/10 flex items-center justify-center">
              <User className="w-5 h-5 text-brand-primary" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white font-display">{employee.funcionario}</h2>
              <p className="text-sm text-brand-gray">{employee.email || 'Sem email'}</p>
            </div>
          </div>
          <button
            aria-label="Fechar detalhes"
            onClick={onClose}
            className="p-2 rounded-lg cursor-pointer hover:bg-brand-dark-lighter transition-colors duration-200 focus-visible:ring-2 focus-visible:ring-brand-primary/50"
          >
            <X className="h-5 w-5 text-brand-gray" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Info grid */}
          <div className="grid grid-cols-2 gap-4">
            <InfoCard
              icon={<Briefcase className="h-4 w-4" />}
              label="Contrato"
              value={CONTRACT_LABELS[employee.contrato || ''] || employee.contrato || '—'}
            />
            <InfoCard
              icon={<Building className="h-4 w-4" />}
              label="Setor"
              value={data?.department?.setor || employee.setorNome || '—'}
            />
            <InfoCard
              icon={<DollarSign className="h-4 w-4" />}
              label="Salário"
              value={formatCurrency(employee.salario)}
            />
            <InfoCard
              icon={<Calendar className="h-4 w-4" />}
              label="Admissão"
              value={formatDate(employee.admissao)}
            />
            {employee.situacao === 'D' && employee.dtdemiss && (
              <InfoCard
                icon={<Calendar className="h-4 w-4" />}
                label="Desligamento"
                value={formatDate(employee.dtdemiss)}
              />
            )}
            <InfoCard
              icon={<Activity className="h-4 w-4" />}
              label="Status"
              value={employee.situacao === 'A' ? 'Ativo' : 'Desligado'}
            />
          </div>

          {/* Benefits (if detail loaded) */}
          {data?.employee && (
            <div>
              <h3 className="text-sm font-medium text-brand-gray mb-3">Benefícios</h3>
              <div className="grid grid-cols-2 gap-2 text-sm">
                {data.employee.op_combustivel && (
                  <BenefitRow label="Combustível" value={data.employee.combustivel} />
                )}
                {data.employee.op_alimenta && (
                  <BenefitRow label="Alimentação" value={data.employee.alimentacao} />
                )}
                {data.employee.saude != null && Number(data.employee.saude) > 0 && (
                  <BenefitRow label="Plano de Saúde" value={data.employee.saude} />
                )}
                {data.employee.AuxTel != null && Number(data.employee.AuxTel) > 0 && (
                  <BenefitRow label="Aux. Telefone" value={data.employee.AuxTel} />
                )}
                {data.employee.pj_r_moradia != null && Number(data.employee.pj_r_moradia) > 0 && (
                  <BenefitRow label="Aux. Moradia" value={data.employee.pj_r_moradia} />
                )}
              </div>
            </div>
          )}

          {/* Vacation balance — computed from database_ferias (PainelGerencial) */}
          {data?.employee && (
            <VacationBalanceSection employee={data.employee as unknown as Record<string, unknown>} />
          )}

          {/* Payroll chart (last 6 months) */}
          <div>
            <h3 className="text-sm font-medium text-brand-gray mb-3">
              Custo Provisionado — Últimos 6 meses
            </h3>
            {isLoading ? (
              <div className="h-48 bg-brand-border/10 rounded-lg animate-pulse" />
            ) : payrollChartData.length === 0 ? (
              <p className="text-sm text-brand-gray/60 text-center py-8">
                Sem dados de provisão disponíveis
              </p>
            ) : (
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={payrollChartData}>
                    <XAxis
                      dataKey="period"
                      tick={{ fill: '#8a8f98', fontSize: 11 }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      tick={{ fill: '#8a8f98', fontSize: 11 }}
                      axisLine={false}
                      tickLine={false}
                      tickFormatter={(v: number) => `${(v / 1000).toFixed(0)}k`}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#24262B',
                        border: '1px solid #2A2D34',
                        borderRadius: '12px',
                        padding: '10px',
                        fontSize: '12px',
                        color: '#ffffff',
                      }}
                      formatter={(value: number) => [formatCurrency(value), 'Custo']}
                    />
                    <Bar
                      dataKey="cprov"
                      fill="#D2FA52"
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          {/* Recent PJ payments (if PJ/SOCIO) */}
          {data?.recentPayments && data.recentPayments.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-brand-gray mb-3">
                Pagamentos PJ Recentes
              </h3>
              <div className="space-y-2">
                {data.recentPayments.map((p, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between px-3 py-2 bg-brand-dark/30 rounded-monumenta text-sm"
                  >
                    <span className="text-brand-gray-light">{formatPeriod(p.mesFolha)}</span>
                    <span className="text-white tabular-nums">{formatCurrency(p.total)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

function InfoCard({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="flex items-start gap-2 p-3 bg-brand-dark/40 rounded-monumenta">
      <span className="text-brand-gray mt-0.5">{icon}</span>
      <div>
        <p className="text-xs text-brand-gray">{label}</p>
        <p className="text-sm text-white font-medium">{value}</p>
      </div>
    </div>
  )
}

function BenefitRow({ label, value }: { label: string; value: number | null }) {
  return (
    <div className="flex items-center justify-between px-3 py-1.5 bg-brand-dark/30 rounded-monumenta">
      <span className="text-brand-gray">{label}</span>
      <span className="text-white tabular-nums">{formatCurrency(value)}</span>
    </div>
  )
}

/**
 * Vacation balance computed from PainelGerencial `database_ferias` column.
 * CLT rule: 30 days per 12-month acquisition period from reference date.
 * For terminated employees, calculates balance up to termination date.
 */
function VacationBalanceSection({ employee }: { employee: Record<string, unknown> }) {
  const referenceDate = employee.database_ferias as string | null
  const geraDiasFerias = employee.gera_dias_ferias as boolean | null
  const admissao = employee.admissao as string | null
  const dtdemiss = employee.dtdemiss as string | null
  const situacao = employee.situacao as string | null

  // Use database_ferias as the vacation accrual start; fallback to admissao
  const startStr = referenceDate || admissao
  if (!startStr || !geraDiasFerias) return null

  const start = new Date(startStr)
  if (isNaN(start.getTime())) return null

  // End date: termination date for dismissed, today for active
  const endDate = situacao === 'D' && dtdemiss ? new Date(dtdemiss) : new Date()

  const DAYS_PER_YEAR = 30
  const MONTHS_PER_YEAR = 12
  const DAYS_PER_MONTH = DAYS_PER_YEAR / MONTHS_PER_YEAR // 2.5

  // Current acquisition period
  const currentYear = endDate.getFullYear()
  const anniversary = new Date(currentYear, start.getMonth(), start.getDate())
  const periodStart = anniversary <= endDate
    ? anniversary
    : new Date(currentYear - 1, start.getMonth(), start.getDate())
  const periodEnd = new Date(periodStart.getFullYear() + 1, periodStart.getMonth(), periodStart.getDate())

  // Months accrued in current period
  const monthsDiff = (endDate.getFullYear() - periodStart.getFullYear()) * 12 + (endDate.getMonth() - periodStart.getMonth())
  const monthsWorked = Math.min(Math.max(monthsDiff, 0), MONTHS_PER_YEAR)
  const daysEarned = monthsWorked >= MONTHS_PER_YEAR ? DAYS_PER_YEAR : Math.round(monthsWorked * DAYS_PER_MONTH * 10) / 10

  // Total tenure
  const totalMonths = (endDate.getFullYear() - start.getFullYear()) * 12 + (endDate.getMonth() - start.getMonth())
  const totalYears = Math.floor(totalMonths / 12)
  const remainderMonths = totalMonths % 12

  // Previous periods (vested, not taken — we can't know used days from PainelGerencial, so show accrued only)
  const completedPeriods = Math.max(0, Math.floor(totalMonths / 12))

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <Palmtree className="h-4 w-4 text-brand-primary" />
        <h3 className="text-sm font-medium text-brand-gray">Saldo de Férias</h3>
      </div>

      {/* Main balance card */}
      <div className="bg-brand-dark/40 rounded-monumenta p-4 mb-3">
        <div className="flex items-baseline justify-between mb-2">
          <span className="text-xs text-brand-gray uppercase tracking-wider">Período atual</span>
          <span className="text-xs text-brand-gray">
            {formatDate(periodStart.toISOString())} — {formatDate(periodEnd.toISOString())}
          </span>
        </div>
        <div className="flex items-baseline gap-2">
          <span className="text-3xl font-bold text-white font-display tabular-nums">{daysEarned}</span>
          <span className="text-sm text-brand-gray">dias adquiridos</span>
        </div>
        <div className="mt-2 h-2 bg-brand-dark-lighter rounded-full overflow-hidden">
          <div
            className="h-full bg-brand-primary rounded-full transition-all duration-500"
            style={{ width: `${(monthsWorked / MONTHS_PER_YEAR) * 100}%` }}
          />
        </div>
        <p className="text-xs text-brand-gray mt-1">
          {monthsWorked} de {MONTHS_PER_YEAR} meses ({monthsWorked >= MONTHS_PER_YEAR ? 'completo' : 'proporcional'})
        </p>
      </div>

      {/* Summary rows */}
      <div className="space-y-2 text-sm">
        <div className="flex items-center justify-between px-3 py-1.5 bg-brand-dark/30 rounded-monumenta">
          <span className="text-brand-gray">Tempo de casa</span>
          <span className="text-white">{totalYears}a {remainderMonths}m</span>
        </div>
        <div className="flex items-center justify-between px-3 py-1.5 bg-brand-dark/30 rounded-monumenta">
          <span className="text-brand-gray">Períodos completos</span>
          <span className="text-white">{completedPeriods}</span>
        </div>
        <div className="flex items-center justify-between px-3 py-1.5 bg-brand-dark/30 rounded-monumenta">
          <span className="text-brand-gray">Data referência férias</span>
          <span className="text-white">{formatDate(startStr)}</span>
        </div>
        {situacao === 'D' && dtdemiss && (
          <div className="flex items-center justify-between px-3 py-1.5 bg-red-500/10 rounded-monumenta">
            <span className="text-red-400">Rescisão</span>
            <span className="text-red-400">{daysEarned} dias a indenizar</span>
          </div>
        )}
      </div>
    </div>
  )
}
